import React, { useState, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import API_URL from '../config/api';
import { getAdminAuthHeader } from '../utils/adminAuth';

function CreatorManagement() {
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchCreators();
  }, []);

  const fetchCreators = async () => {
    try {
      setLoading(true);
      setError(null);
      const authHeader = getAdminAuthHeader();
      
      const response = await fetch(`${API_URL}/admin/creators`, {
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch creators');
      }

      const data = await response.json();
      // Sort by order
      const sortedCreators = [...data].sort((a, b) => a.order - b.order);
      setCreators(sortedCreators);
      setHasChanges(false);
    } catch (err) {
      console.error('Error fetching creators:', err);
      setError('Failed to load creators');
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setCreators((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        const newCreators = arrayMove(items, oldIndex, newIndex);
        // Update order values
        const updatedCreators = newCreators.map((creator, index) => ({
          ...creator,
          order: index + 1,
        }));
        setHasChanges(true);
        return updatedCreators;
      });
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      const authHeader = getAdminAuthHeader();

      // Prepare updates: order
      const updates = creators.map((creator) => ({
        id: creator.id,
        order: creator.order,
      }));

      const response = await fetch(`${API_URL}/admin/creators/update`, {
        method: 'PUT',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ creators: updates }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save changes');
      }

      setSuccess('Creators updated successfully!');
      setHasChanges(false);
      
      // Refresh creators to get latest data
      setTimeout(() => {
        fetchCreators();
      }, 1000);
    } catch (err) {
      console.error('Error saving creators:', err);
      setError(err.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-white/60">Loading creators...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white mb-1">Creator Management</h2>
          <div className="text-white/40 text-xs uppercase tracking-wider">Reorder creators</div>
        </div>
        <button
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className="px-6 py-3 bg-accent-blue/20 border border-accent-blue/30 rounded-xl text-accent-blue font-medium hover:bg-accent-blue/30 hover:border-accent-blue/40 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-400">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 text-green-400">
          {success}
        </div>
      )}

      {hasChanges && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 text-yellow-400">
          You have unsaved changes. Click "Save Changes" to apply them.
        </div>
      )}

      {/* Creator Ordering Section */}
      <div className="bg-dark-card border border-dark-border rounded-lg p-6">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-white mb-1">Creator Order</h3>
          <div className="text-white/60 text-sm mb-2">
            Drag and drop creators to reorder them.
          </div>
        </div>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={creators.map((creator) => creator.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-3">
              {creators
                .sort((a, b) => a.order - b.order)
                .map((creator) => (
                  <SortableCreatorItem
                    key={creator.id}
                    creator={creator}
                  />
                ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
}

function SortableCreatorItem({ creator }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: creator.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-dark-bg/30 border border-dark-border rounded-lg p-4 ${
        isDragging ? 'shadow-lg' : ''
      } hover:border-white/20 transition-all`}
    >
      <div className="flex items-center gap-4">
        {/* Drag Handle */}
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-white/40 hover:text-white/60 transition-colors"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <circle cx="5" cy="5" r="1.5" />
            <circle cx="5" cy="10" r="1.5" />
            <circle cx="5" cy="15" r="1.5" />
            <circle cx="10" cy="5" r="1.5" />
            <circle cx="10" cy="10" r="1.5" />
            <circle cx="10" cy="15" r="1.5" />
            <circle cx="15" cy="5" r="1.5" />
            <circle cx="15" cy="10" r="1.5" />
            <circle cx="15" cy="15" r="1.5" />
          </svg>
        </div>

        {/* Order Number */}
        <div className="w-10 text-center">
          <div className="text-white/40 text-xs uppercase tracking-wider mb-1">Order</div>
          <div className="text-white font-mono font-bold text-lg">{creator.order}</div>
        </div>

        {/* Creator Image */}
        {creator.pfp && (
          <img
            src={creator.pfp}
            alt={creator.name}
            className="w-16 h-16 rounded-lg object-cover"
          />
        )}

        {/* Creator Info */}
        <div className="flex-1 min-w-0">
          <h3 className="text-white font-semibold text-lg truncate mb-1">{creator.name}</h3>
          <div className="text-white/30 text-xs mt-1">
            ID: {creator.id}
            {creator.itchLink && (
              <span className="ml-2">
                | <a href={creator.itchLink} target="_blank" rel="noopener noreferrer" className="text-accent-blue hover:underline">Itch.io</a>
              </span>
            )}
            {creator.patreonLink && (
              <span className="ml-2">
                | <a href={creator.patreonLink} target="_blank" rel="noopener noreferrer" className="text-accent-blue hover:underline">Patreon</a>
              </span>
            )}
            {creator.subscribestarLink && (
              <span className="ml-2">
                | <a href={creator.subscribestarLink} target="_blank" rel="noopener noreferrer" className="text-accent-blue hover:underline">SubscribeStar</a>
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreatorManagement;

